import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, eur, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ClipboardList, Search, Eye, FileText } from "lucide-react";
import { t, tf } from "@/lib/i18n";

const STATUS = {
  bozza: { label: t("Bozza"), cls: "bg-muted text-muted-foreground border-border" },
  confermato: { label: t("Confermato"), cls: "bg-primary/20 text-primary border-primary/30" },
  evaso: { label: t("Evaso"), cls: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  annullato: { label: t("Annullato"), cls: "bg-red-100 text-red-700 border-red-200" },
};
const FILTERS = [["tutti", t("Tutti")], ["bozza", t("Bozze")], ["confermato", t("Confermati")], ["evaso", t("Evasi")]];

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isCompany = user?.role === "company";
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("tutti");
  const [detail, setDetail] = useState(null);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["orders"],
    queryFn: async () => (await api.get("/orders")).data,
  });

  const changeStatus = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/orders/${id}/status`, { status })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success(t("Stato aggiornato"));
    },
  });

  const openPdf = (id) => window.open(`${BACKEND_URL}/api/orders/${id}/pdf`, "_blank");

  const filtered = orders.filter((o) => {
    const matchText =
      o.customer?.name?.toLowerCase().includes(search.toLowerCase()) ||
      o.order_no?.toLowerCase().includes(search.toLowerCase()) ||
      o.agent_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "tutti" || o.status === statusFilter;
    return matchText && matchStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("Ordini")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{orders.length} {t("ordini registrati")}</p>
        </div>
        <Button onClick={() => navigate("/orders/new")} className="gap-2 font-semibold" data-testid="orders-new-btn">
          <Plus className="h-4 w-4" /> {t("Nuovo ordine")}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="order-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Cerca cliente, numero, agente")} className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              data-testid={`order-filter-${key}`}
              className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                statusFilter === key ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground hover:border-primary/40"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">{t("Caricamento…")}</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-12 text-center">
          <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("Nessun ordine ancora.")}</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">{t("Numero")}</th>
                  <th className="text-left px-5 py-3 font-medium">{t("Cliente")}</th>
                  {isCompany && <th className="text-left px-5 py-3 font-medium">{t("Agente")}</th>}
                  <th className="text-left px-5 py-3 font-medium">{t("Data")}</th>
                  <th className="text-left px-5 py-3 font-medium">{t("Stato")}</th>
                  <th className="text-right px-5 py-3 font-medium">{t("Totale")}</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.order_id} className="border-t border-border/60 hover:bg-muted/20" data-testid={`order-row-${o.order_id}`}>
                    <td className="px-5 py-3 font-mono-j text-xs">{o.order_no}</td>
                    <td className="px-5 py-3 font-medium">{o.customer?.name}</td>
                    {isCompany && <td className="px-5 py-3 text-muted-foreground">{o.agent_name}</td>}
                    <td className="px-5 py-3 text-muted-foreground">{o.created_at?.slice(0, 10)}</td>
                    <td className="px-5 py-3">
                      <Select value={o.status} onValueChange={(v) => changeStatus.mutate({ id: o.order_id, status: v })}>
                        <SelectTrigger className={`h-8 w-[140px] text-xs font-medium border ${STATUS[o.status]?.cls || ""}`} data-testid={`status-select-${o.order_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-3 text-right font-semibold text-primary">{eur(o.grand_total)}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => openPdf(o.order_id)} data-testid={`pdf-order-${o.order_id}`}>
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDetail(o)} data-testid={`view-order-${o.order_id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-mono-j text-base">{detail?.order_no}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="bg-muted/30 rounded-xl p-4 space-y-1">
                <p className="font-semibold">{detail.customer?.name}</p>
                {detail.customer?.vat && <p className="text-muted-foreground">{t("P.IVA/CF:")} {detail.customer.vat}</p>}
                {detail.customer?.address && <p className="text-muted-foreground">{detail.customer.address}</p>}
                {detail.customer?.payment_terms && <p className="text-muted-foreground">{t("Pagamento:")} {detail.customer.payment_terms}</p>}
                <p className="text-muted-foreground">{t("Agente:")} {detail.agent_name}</p>
              </div>
              <div className="space-y-2">
                {detail.items?.map((it, idx) => (
                  <div key={idx} className="flex justify-between border-b border-border/60 pb-2">
                    <div>
                      <p className="font-medium">{it.name} × {it.quantity} {t("pz")}</p>
                      {it.packs > 0 && <p className="text-xs text-muted-foreground" data-testid={`order-item-packs-${it.product_id}`}>{tf("{c} colli da {p} + {l} sfusi", { c: it.packs, p: it.pack_size, l: it.loose })}</p>}
                      <p className="text-xs text-muted-foreground font-mono-j">{it.code} {t("· IVA")} {it.vat_rate}%{it.discount ? ` · -${it.discount}%` : ""}</p>
                    </div>
                    <span className="font-medium">{eur(it.price * it.quantity * (1 - (it.discount || 0) / 100))}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-1 pt-2">
                <Row label={t("Imponibile")} value={eur(detail.net_total)} />
                <Row label={t("Sconti")} value={`− ${eur(detail.discount_total)}`} />
                <Row label={t("IVA")} value={eur(detail.vat_total)} />
                <div className="flex justify-between font-display text-lg font-extrabold pt-2 border-t border-border">
                  <span>{t("Totale")}</span>
                  <span className="text-primary">{eur(detail.grand_total)}</span>
                </div>
              </div>
              {detail.notes && <p className="text-muted-foreground text-xs">{t("Note:")} {detail.notes}</p>}
              <Button variant="outline" className="w-full gap-2" onClick={() => openPdf(detail.order_id)} data-testid="detail-pdf-btn">
                <FileText className="h-4 w-4" /> {t("Scarica / Stampa PDF")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between text-muted-foreground">
    <span>{label}</span>
    <span>{value}</span>
  </div>
);
